import { Badge, Box, Paper, Skeleton, Table, Anchor } from "@mantine/core";
import React, { useState } from "react";
import { AmountDisplay } from "../Components/AmountDisplay";
import { ContractDisplay } from "../Components/ContractDisplay";
import { useGlobalStatusContext } from "../GlobalStore";
import { useIsSmallScreen } from "../Components/Utils";
import { SortTh, Td, TdText, Th, ThText, Tr } from "../Components/EpTable";
import { EpAlert } from "../Components/EpMessage";
import { EpTxTypeNew } from "../Components/EpTxType";
import dayjs from "dayjs";
import EpPopover from "../Components/EpPopover";
import { useFireStoreQuery } from "../Components/useFireStoreQuery";
import { v4 as uuid } from "uuid";
import { FromNow } from "../Components/FromNow";
import { ROUTER_BASE } from "../Components/Link";

const Time = ({ timestamp }: { timestamp: number }) => {
  return (
    <EpPopover
      position="left"
      target={
        <Box>
          <Badge
            radius={6}
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "#F3F6F9",
              cursor: "pointer",
            })}
          >
            <TdText sx={{ fontSize: 12 }}>
              {dayjs(timestamp * 1000).format("YYYY-MM-DD")}
            </TdText>
          </Badge>
        </Box>
      }
      withArrow
    >
      <TdText
        size="sm"
        sx={(theme) => ({
          color:
            theme.colorScheme === "dark" ? theme.white : theme.colors.dark[6],
        })}
      >
        {dayjs.utc((timestamp ?? 0) * 1000).format("MMM D, YYYY HH:mm:ss")}
        (UTC)
      </TdText>
    </EpPopover>
  );
};

export const TransactionLeaderBoard = React.memo(function ({
  chain,
  address,
  showFrom,
  showContract,
  hideRevenue,
  hideTx,
}: {
  chain: string;
  address?: string;
  showFrom?: boolean;
  showContract?: boolean;
  hideRevenue?: boolean;
  hideTx?: boolean;
}) {
  const { period } = useGlobalStatusContext();
  return (
    <TransactionLeaderBoardPeriod
      period={period}
      chain={chain}
      address={address}
      showFrom={!!showFrom}
      showContract={!!showContract}
      hideRevenue={hideRevenue}
      hideTx={hideTx}
    />
  );
});

export function TransactionLeaderBoardPeriod({
  period,
  chain,
  address,
  showFrom,
  showContract,
  hideRevenue,
  hideTx,
}: {
  period: number | string;
  chain: string;
  address?: string;
  showFrom?: boolean;
  showContract: boolean;
  hideRevenue?: boolean;
  hideTx?: boolean;
}) {
  const isSmallScreen = useIsSmallScreen();

  const [bySortingKey, setBySortingKey] = useState("profit");
  const [bySortingDirec, setBySortingDirec] = useState("desc");

  const { data, isLoading } = useFireStoreQuery<HomeTxLeaderBoard>({
    path: `/${chain}/${period}d/tx_profit_leaderboard/${bySortingKey}_${bySortingDirec}/default`,
    limit: 10,
  });

  if (!isLoading && !data?.data?.length) {
    return <EpAlert>Data Not Found!</EpAlert>;
  }

  let colcount = isSmallScreen ? 4 : 6;
  if (!showContract) {
    colcount--;
  }
  if (hideRevenue) {
    colcount--;
  }
  if (hideTx) {
    colcount--;
  }

  const rows = isLoading
    ? Array.from({ length: 10 }).map((_) => (
        <tr key={"TLB" + uuid()}>
          {Array.from({ length: colcount }).map((_, idx) => (
            <Td key={`skeleton-column-${idx}`}>
              <Skeleton width="100%" height={18} />
            </Td>
          ))}
        </tr>
      ))
    : data?.data.map((item, idx) => (
        <Tr
          // to={
          //   item.type === "Liquidation"
          //     ? `/${chain}/liquidation/tx/${item.txHash}`
          //     : `/${chain}/tx/${item.txHash}`
          // }
          key={idx}
          style={{
            cursor: "pointer",
          }}
        >
          <Td>
            <Anchor
              sx={{
                width: "100%",
                display: "contents",
                "&:hover": {
                  textDecoration: "none",
                },
              }}
              href={
                item.type === "Liquidation"
                  ? `/${ROUTER_BASE}/${chain}/liquidation/tx/${item.txHash}`
                  : `/${ROUTER_BASE}/${chain}/tx/${item.txHash}`
              }
            >
              <EpTxTypeNew chain={chain} tx={item} noWrap />
            </Anchor>
          </Td>
          {showContract && !isSmallScreen ? (
            <Td>
              <Anchor
                key={`Anchor_${idx}`}
                sx={{
                  width: "100%",
                  display: "contents",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
                href={
                  item.type === "Liquidation"
                    ? `/${ROUTER_BASE}/${chain}/liquidation/tx/${item.txHash}`
                    : `/${ROUTER_BASE}/${chain}/tx/${item.txHash}`
                }
              >
                <ContractDisplay
                  address={item.contract}
                  labelList={item.contractLabels}
                  chain={chain}
                />
              </Anchor>
            </Td>
          ) : null}
          <Td align="right">
            <Anchor
              key={`Anchor_${idx}`}
              sx={{
                width: "100%",
                display: "contents",
                "&:hover": {
                  textDecoration: "none",
                },
              }}
              href={
                item.type === "Liquidation"
                  ? `/${ROUTER_BASE}/${chain}/liquidation/tx/${item.txHash}`
                  : `/${ROUTER_BASE}/${chain}/tx/${item.txHash}`
              }
            >
              <AmountDisplay value={item.profit} prefix="$" />
            </Anchor>
          </Td>
          {isSmallScreen ? null : (
            <Td align="right">
              <Anchor
                key={`Anchor_${idx}`}
                sx={{
                  width: "100%",
                  display: "contents",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
                href={
                  item.type === "Liquidation"
                    ? `/${ROUTER_BASE}/${chain}/liquidation/tx/${item.txHash}`
                    : `/${ROUTER_BASE}/${chain}/tx/${item.txHash}`
                }
              >
                <Time timestamp={item.blockTimestamp} />
              </Anchor>
            </Td>
          )}
        </Tr>
      ));

  return (
    <>
      <Paper sx={{ overflow: "auto" }}>
        <Table highlightOnHover horizontalSpacing={20}>
          {/* <colgroup>
            {Array.from({ length: colcount }).map((_) => (
              <col style={{ width: `${100 / colcount}%` }} />
            ))}
          </colgroup> */}
          <thead>
            <tr>
              <Th>
                <ThText>Type</ThText>
              </Th>
              {showContract && !isSmallScreen ? (
                <Th>
                  <ThText>Contract</ThText>
                </Th>
              ) : null}
              {/* <Th style={{ textAlign: "right" }}>
                <ThText>Profit</ThText>
              </Th> */}
              <SortTh
                itemKey="profit"
                itemName="Profit"
                activeKey={bySortingKey}
                style={{ textAlign: "right" }}
                direc
                onClick={(itemKey, nextDirec) => {
                  setBySortingKey(itemKey);
                  setBySortingDirec(nextDirec);
                }}
              />
              {isSmallScreen ? null : (
                <Th style={{ textAlign: "right" }}>
                  <ThText>Time(UTC)</ThText>
                </Th>
              )}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      </Paper>
      <Box
        sx={(theme) => ({
          padding: "9px 20px",
          borderTop:
            theme.colorScheme === "dark"
              ? `1px solid ${theme.colors.dark[6]}`
              : `1px solid ${theme.colors.gray[2]}`,
        })}
      >
        <FromNow
          isLoading={isLoading}
          updateTimestamp={data?.updateTimestamp!}
          updateBlockNumber={data?.updateBlockNumber!}
        />
      </Box>
    </>
  );
}

export default TransactionLeaderBoard;
